/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ActionButton } from '@fluentui/react/lib/Button';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { Template } from '../../../../../utilities/TemplateUtils';
import { ScrollBoundary } from '../../../../common';
import { ErrorPage } from '../../../../containers/ErrorPage';
import { LoadingPage } from '../../../../containers/LoadingPage';
import { TemplateCardList } from '../../../TemplateCardList';
import { getListContainerStyles, getPageStyles } from '../../PreSetupTemplateWizard.styles';
import { getBodyStyles, getScrollContainerStyle, getTitleStyles } from './TemplateStore.styles';

export interface TemplateStoreComponentProps {
    isLoading?: boolean;
    showError?: boolean;
    templates: Template[];
    onTemplateClick?: (template: Template) => void;
    onAddFromScratchClick?: () => void;
}

export const TemplateStoreComponent: React.FC<TemplateStoreComponentProps> = (
    props: TemplateStoreComponentProps
): JSX.Element => {
    const loc = getAppHost().getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;

    const { isLoading, showError, templates, onTemplateClick, onAddFromScratchClick } = props;

    return (
        <Stack styles={getPageStyles}>
            <Stack.Item>
                <Label role='alert' aria-label={loc.strings.select_template_text} styles={getTitleStyles(theme)}>
                    {loc.strings.select_template_text}
                </Label>
            </Stack.Item>

            <Stack.Item grow styles={getBodyStyles}>
                {isLoading ? (
                    <LoadingPage />
                ) : showError ? (
                    <ErrorPage message={window.navigator.onLine ? null : loc.strings.upload_error_network_offline} />
                ) : (
                    <div style={getScrollContainerStyle()}>
                        <ScrollBoundary isTopBoundary={true} />
                        <Stack styles={getListContainerStyles}>
                            <TemplateCardList
                                sortCategory
                                sortTemplates
                                templates={templates}
                                onCardClick={onTemplateClick}
                            />
                        </Stack>
                        <ScrollBoundary isTopBoundary={false} />
                    </div>
                )}
            </Stack.Item>

            {!showError && (
                <Stack.Item>
                    <ActionButton
                        text={loc.strings.add_from_scratch}
                        ariaLabel={loc.strings.add_from_scratch}
                        onClick={onAddFromScratchClick}
                        iconProps={{ iconName: 'Add' }}
                    />
                </Stack.Item>
            )}
        </Stack>
    );
};
